import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router';

import {
  breakpoints,
  PictogramRegularNoSearch,
  useBreakpointValue,
} from '@notino/react-styleguide';
import { AvailabilityState } from '@notino/shared/definitions/types';

import { useFetchProductAlternatives } from '@containers/ProductDetailContainer/hooks/useFetchProductAlternatives';
import { useIsMounted } from '@hooks/useIsMounted';
import { PRODUCT_ALTERNATIVES_ELEMENT_ID } from '@lib/constants';
import { css } from '@linaria/core';
import { styled } from '@linaria/react';

import { useProductDetailContext } from '../../ProductDetailContext';
import { ProductsSection } from '../ProductsSection';

import { messages } from './messages';
import {
  NoAlternativesDescription,
  NoAlternativesTitle,
  NoAlternativesWrap,
  Title,
} from './styled';

const MAX_PRODUCTS = 12;

type Props = {
  showOn: 'mobile' | 'desktop';
};

const Wrap = styled.div`
  margin: 3.5rem 0 2rem;
  @media (min-width: ${breakpoints.lg}) {
    margin: 4.5rem 0 0.5rem;
  }
`;

export const ProductAlternatives = ({ showOn }: Props) => {
  const { hash } = useLocation();
  const isMounted = useIsMounted();
  const isDesktop = useBreakpointValue({ lg: true }, { ssrValue: false });
  const isDesiredViewPort = showOn === 'desktop' ? isDesktop : !isDesktop;
  const { product } = useProductDetailContext();

  const everyProductUnavailable = product.variants.every(
    (v) => v.availability.state !== AvailabilityState.CanBeBought
  );

  const { data, loading } = useFetchProductAlternatives();

  const shouldRender =
    everyProductUnavailable && isMounted && isDesiredViewPort;

  const hasShowAlternativesHash =
    hash === `#${PRODUCT_ALTERNATIVES_ELEMENT_ID}`;

  React.useEffect(() => {
    if (shouldRender && hasShowAlternativesHash) {
      setTimeout(() => {
        document
          .getElementById(PRODUCT_ALTERNATIVES_ELEMENT_ID)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 10);
    }
  }, [shouldRender, hasShowAlternativesHash]);

  if (!shouldRender) {
    return null;
  }

  const hasNoAlternatives =
    data?.getOutOfStockAlternatives?.recommendedProducts?.length === 0;

  // only show this when redirected from other page with the hash
  if (hasNoAlternatives && hasShowAlternativesHash) {
    return (
      <Wrap id={PRODUCT_ALTERNATIVES_ELEMENT_ID}>
        <Title>
          <FormattedMessage {...messages.title} />
        </Title>

        <NoAlternativesWrap>
          <PictogramRegularNoSearch
            width="72px"
            height="72px"
            color="icon.disabled"
          />
          <NoAlternativesTitle>
            <FormattedMessage {...messages.noAlternativesTitle} />
          </NoAlternativesTitle>
          <NoAlternativesDescription>
            <FormattedMessage {...messages.noAlternativesSubtitle} />
          </NoAlternativesDescription>
        </NoAlternativesWrap>
      </Wrap>
    );
  }

  return (
    <div
      id={PRODUCT_ALTERNATIVES_ELEMENT_ID}
      data-testid="product-alternatives-wrapper"
      className={css`
        position: relative;
        left: -0.9375rem;
        width: 100vw;
        @media (min-width: ${breakpoints.lg}) {
          width: auto;
          position: static;
        }
      `}
    >
      <Wrap>
        <ProductsSection
          productIds={
            data?.getOutOfStockAlternatives?.recommendedProducts
              ?.map((x) => x?.productId ?? '')
              .filter(Boolean)
              .slice(0, MAX_PRODUCTS) ?? []
          }
          productIdsLoading={loading}
          titleMessage={messages.title}
          titleId="pdProductAlternatives"
          containerId="pd-product-alternatives-header"
          listLabel="product_alternatives"
          withDivider={false}
          trackProductClick={true}
          trackProductImpression={true}
          attributionToken={
            data?.getOutOfStockAlternatives?.recommendationAttributes
              ?.attributionToken ?? undefined
          }
          itemsPerSlide={3}
          titleClassName={css`
            @media (min-width: ${breakpoints.lg}) {
              padding-left: 0;
            }
          `}
          listContentClassName={css`
            padding: 0;
          `}
        />
      </Wrap>
    </div>
  );
};
